










































































































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Validations } from "vuelidate-property-decorators";
import { required, email } from "vuelidate/lib/validators";
import { phone } from "@/validators/custom";
import FormValidation from "@/components/elements/form/FormValidation.vue";
import DuplicateCustomer from "@/components/elements/modal/DuplicateCustomer.vue";
import { EventBus } from "@/event-bus";
import {
  DuplicateCustomerMatch,
  NetSuiteCustomerContact
} from "@/models/netsuite/netsuite";
import NetSuiteService from "@/services/netsuite_service";

@Component({
  components: {
    DuplicateCustomer,
    FormValidation
  }
})
export default class Contact extends Vue {
  @Prop() quote_name!: string;
  @Prop() first_name!: string;
  @Prop() last_name!: string;
  @Prop() company_name!: string;
  @Prop({ default: "" }) title!: string;
  @Prop() phone!: string;
  @Prop() email!: string;
  @Prop({ default: () => [] }) contacts!: NetSuiteCustomerContact[];
  @Prop({ default: true }) isNewCustomer!: boolean;
  @Prop({ default: false }) send_email!: boolean;
  @Prop({ default: null }) contact_id!: number | null;

  protected selectedContactId: number | "new" | null = null;
  protected submitted = false;

  protected duplicateMatches: DuplicateCustomerMatch[] = [];
  protected showDuplicateModal = false;
  /** The company+email the rep said was a different company, so re-asking about
   *  the same values would just be in their way. Any edit revives the check. */
  private dismissedFor: string | null = null;
  private duplicateTimer: number | null = null;
  /** Guards against a slow early response landing after a later, newer one. */
  private duplicateRequestId = 0;
  private netSuiteService = new NetSuiteService();

  get visibleDuplicates(): DuplicateCustomerMatch[] {
    return this.duplicateMatches.slice(0, 5);
  }

  /** Identity of the values a check (or a dismissal) applies to. */
  private get duplicateKey(): string {
    return `${(this.company_name ?? "").trim().toLowerCase()}|${(
      this.email ?? ""
    )
      .trim()
      .toLowerCase()}`;
  }

  @Watch("company_name")
  onCompanyNameInput(): void {
    this.scheduleDuplicateCheck();
  }

  @Watch("email")
  onEmailInput(): void {
    this.scheduleDuplicateCheck();
  }

  @Watch("isNewCustomer")
  onCustomerKindChange(): void {
    // Switching to an existing customer answers the question outright.
    this.closeDuplicateModal();
    this.dismissedFor = null;
    this.scheduleDuplicateCheck();
  }

  /**
   * Ask NetSuite whether this company is already there, once the rep pauses.
   *
   * Debounced because it runs off every keystroke in two fields and each call is
   * a SuiteQL round trip. A company name under three characters is not worth
   * searching on — the token would match half the account — and a half-typed
   * email address matches nothing, so both are left alone until they look real.
   */
  private scheduleDuplicateCheck(): void {
    if (this.duplicateTimer !== null) {
      window.clearTimeout(this.duplicateTimer);
      this.duplicateTimer = null;
    }
    if (!this.isNewCustomer) {
      this.closeDuplicateModal();
      return;
    }
    const companyName = (this.company_name ?? "").trim();
    const typedEmail = (this.email ?? "").trim();
    // A half-typed address is not worth a lookup of its own, and sending it
    // would have NetSuite search for it anyway.
    const email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typedEmail)
      ? typedEmail
      : "";
    if (companyName.length < 3 && email === "") {
      this.closeDuplicateModal();
      return;
    }
    this.duplicateTimer = window.setTimeout(() => {
      this.duplicateTimer = null;
      this.runDuplicateCheck(companyName, email);
    }, 600);
  }

  private async runDuplicateCheck(
    companyName: string,
    email: string
  ): Promise<void> {
    const requestId = ++this.duplicateRequestId;
    const matches = await this.netSuiteService.checkDuplicateCustomers(
      companyName,
      email
    );
    if (requestId !== this.duplicateRequestId) return;
    this.duplicateMatches = matches;
    // Raised the moment there is something to answer — except on values the rep
    // has already told us are a different company.
    this.showDuplicateModal =
      matches.length > 0 && this.dismissedFor !== this.duplicateKey;
  }

  private closeDuplicateModal(): void {
    this.showDuplicateModal = false;
    this.duplicateMatches = [];
  }

  /** "This is a different company": their answer stands for these values. */
  protected dismissDuplicates(): void {
    this.dismissedFor = this.duplicateKey;
    this.closeDuplicateModal();
  }

  /**
   * "Use this customer": the parent owns the switch — it needs the customer
   * list and the store actions this form has no business holding.
   */
  protected onUseExistingCustomer(match: DuplicateCustomerMatch): void {
    this.closeDuplicateModal();
    this.$emit("useExistingCustomer", match);
  }

  @Watch("$v.$invalid", { deep: true })
  onFormChange(): void {
    if (this.submitted) {
      this.validateForm();
    }
  }

  @Validations()
  validations() {
    return {
      first_name: { required },
      last_name: { required },
      quote_name: { required },
      ...(this.isNewCustomer ? { company_name: { required } } : {}),
      phone: { phone, required },
      email: { email, required }
    };
  }

  created() {
    EventBus.$on("submitCheckoutForm", this.validateForm);
    EventBus.$on("saveQuote", this.validateForm);
    // A form that arrives already filled in (back from Review, a resubmit)
    // still deserves the check — nothing has been typed to trigger it.
    this.scheduleDuplicateCheck();
    // Contacts usually arrive after mount (they ride along with the customer
    // fetch), so this first pass is a best-effort — onContactsLoaded runs the
    // same match once the list is populated.
    if (!this.syncSelectedContact()) {
      this.selectedContactId = "new";
    }
  }

  @Watch("contacts")
  onContactsLoaded(contacts: NetSuiteCustomerContact[]): void {
    // Editing a quote whose contact already exists in NetSuite: point the
    // dropdown at that contact instead of leaving it on "+ Create New Contact".
    if (this.syncSelectedContact()) return;
    if (contacts.length === 1) {
      this.selectedContactId = contacts[0].id;
      this.onContactSelect();
      return;
    }
    // Nothing in the new list matches the current selection. That is what a
    // customer switch looks like from here: the parent has blanked the contact
    // fields and handed down the incoming customer's contacts, but this
    // component's own `selectedContactId` still holds an id belonging to the
    // customer that was just replaced. Left alone the <select> binds to a value
    // that isn't among its options and renders blank, so the dropdown reads as
    // "nothing chosen" while still reporting a stale id. Fall back to the
    // explicit "+ Create New Contact" option, which is what an empty contact
    // block actually means.
    this.selectedContactId = "new";
  }

  /**
   * Select the dropdown option for the quote's saved contact when it matches a
   * NetSuite contact — by id first, then by email (a contact created on the
   * quote's first save comes back with an id, but matching email covers ids
   * that drifted). Returns whether a match was selected. Deliberately does NOT
   * call onContactSelect(): the saved header already holds the correct field
   * values, and re-applying the NetSuite record could clobber edits.
   */
  private syncSelectedContact(): boolean {
    if (!this.contacts.length) return false;
    let match: NetSuiteCustomerContact | undefined;
    if (this.contact_id != null) {
      match = this.contacts.find(c => c.id === this.contact_id);
    }
    if (!match && this.email) {
      const email = this.email.toLowerCase();
      match = this.contacts.find(c => c.email?.toLowerCase() === email);
    }
    if (match) {
      this.selectedContactId = match.id;
      return true;
    }
    return false;
  }

  beforeDestroy() {
    EventBus.$off("submitCheckoutForm");
    EventBus.$off("saveQuote");
    if (this.duplicateTimer !== null) {
      window.clearTimeout(this.duplicateTimer);
    }
  }

  get sortedContacts(): NetSuiteCustomerContact[] {
    return [...this.contacts].sort((a, b) => {
      const nameA = [a.firstname, a.lastname]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const nameB = [b.firstname, b.lastname]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const hasNameA = nameA.length > 0;
      const hasNameB = nameB.length > 0;
      if (hasNameA !== hasNameB) return hasNameA ? -1 : 1;
      return (nameA || a.email.toLowerCase()).localeCompare(
        nameB || b.email.toLowerCase()
      );
    });
  }

  protected formatContactOption(contact: NetSuiteCustomerContact): string {
    const parts: string[] = [];
    const name = [contact.firstname, contact.lastname]
      .filter(Boolean)
      .join(" ");
    if (name) parts.push(name);
    if (contact.email) parts.push(contact.email);
    if (contact.phone) parts.push(contact.phone);
    return parts.join(" | ");
  }

  protected onContactSelect(): void {
    if (this.selectedContactId === "new" || !this.selectedContactId) {
      this.$emit("update:contact_id", null);
      this.updateItem("", "title");
      this.updateItem("", "first_name");
      this.updateItem("", "last_name");
      this.updateItem("", "phone");
      this.updateItem("", "email");
      return;
    }
    const contact = this.contacts.find(c => c.id === this.selectedContactId);
    if (!contact) return;
    this.$emit("update:contact_id", contact.id);
    this.updateItem(contact.title || "", "title");
    this.updateItem(contact.firstname || "", "first_name");
    this.updateItem(contact.lastname || "", "last_name");
    this.updateItem(contact.phone || "", "phone");
    this.updateItem(contact.email || "", "email");
  }

  protected validateForm(): void {
    this.submitted = true;
    this.$v.$touch();
    this.$emit("sendError", this.$v.$invalid, "contact");
  }

  /**
   * Have to explicitly call vuelidate ($v) methods if not using v-model
   */
  public updateItem(fieldValue: string, fieldName: string) {
    this.$emit(`update:${fieldName}`, fieldValue);
    if (this.submitted && this.$v[fieldName]) {
      this.$v[fieldName]!.$touch();
      this.validateForm();
    }
  }
}
